import { mapRange, splitSelection } from './anchors';

// ── From a browser selection to verse offsets ────────────────────────────────
// Each drawn verse is a <span class="anno-verse">; AnnotatedVerse registers
// what it stands for here. A selection is read by counting the characters of
// the verse's own text nodes up to each end, skipping ref markers (<sup>) and
// anything marked .anno-skip, so the count lands on the same offsets the
// stored text uses whatever is drawn in between.

const info = new WeakMap();   // element → { book, chapter, verse, lang, plain, display }

export function registerVerse(el, data) {
  if (el) info.set(el, data);
}

const skipped = node => !!node.parentElement?.closest('sup, .anno-skip');

function textNodes(el) {
  const out = [];
  const walker = document.createTreeWalker(el, NodeFilter.SHOW_TEXT, {
    acceptNode: n => (skipped(n) ? NodeFilter.FILTER_REJECT : NodeFilter.FILTER_ACCEPT),
  });
  for (let n = walker.nextNode(); n; n = walker.nextNode()) out.push(n);
  return out;
}

// Characters of `el`'s counted text before the point (node, offset).
export function offsetIn(el, node, offset) {
  const point = document.createRange();
  point.setStart(node, offset);
  let total = 0;
  for (const t of textNodes(el)) {
    if (t === node) return total + offset;
    // Wholly before the point: count it. Starts after: stop.
    if (point.comparePoint(t, t.length) <= 0) total += t.length;
    else if (point.comparePoint(t, 0) >= 0) break;
  }
  return total;
}

// Which parallel column an element is in (0 or 1), or -1 outside parallel mode.
function columnOf(el) {
  const col = el.closest('.parallel-col');
  return col ? Array.prototype.indexOf.call(col.parentNode.children, col) : -1;
}

// The current selection inside `root` as per-verse ranges in plain-text
// offsets (see splitSelection), or null when it covers no verse text.
// A drag stays in the column it started in: in parallel mode the rows
// interleave both languages, so a range from one verse to the next also spans
// the other column's verse between them.
export function readSelection(root) {
  const sel = window.getSelection?.();
  if (!sel || sel.isCollapsed || !sel.rangeCount || !root) return null;
  const range = sel.getRangeAt(0);
  if (!range.intersectsNode(root)) return null;
  const verses = [...root.querySelectorAll('.anno-verse')].filter(el => info.has(el) && range.intersectsNode(el));
  if (!verses.length) return null;
  const first = verses.find(el => el.contains(range.startContainer)) || verses[0];
  const column = columnOf(first);
  const lang = info.get(first).lang;
  const hits = [];
  for (const el of verses) {
    const v = info.get(el);
    if (v.lang !== lang || columnOf(el) !== column) continue;
    let s = el.contains(range.startContainer) ? offsetIn(el, range.startContainer, range.startOffset) : 0;
    let e = el.contains(range.endContainer) ? offsetIn(el, range.endContainer, range.endOffset) : v.display.length;
    if (e <= s) continue;
    // Offsets were counted on what is drawn; stored marks use the plain text.
    const mapped = mapRange(v.display, v.plain, s, e);
    if (!mapped) continue;
    [s, e] = mapped;
    hits.push({ book: v.book, chapter: v.chapter, verse: v.verse, lang: v.lang, text: v.plain, start: s, end: e });
  }
  const ranges = splitSelection(hits);
  return ranges.length ? ranges : null;
}

// The on-screen box around everything drawn for one mark.
export function groupRect(root, groupId) {
  if (!root) return null;
  const els = root.querySelectorAll(`[data-anno-group="${groupId}"]`);
  let box = null;
  els.forEach(el => {
    for (const r of el.getClientRects()) {
      if (!r.width && !r.height) continue;
      box = box
        ? { left: Math.min(box.left, r.left), top: Math.min(box.top, r.top),
            right: Math.max(box.right, r.right), bottom: Math.max(box.bottom, r.bottom) }
        : { left: r.left, top: r.top, right: r.right, bottom: r.bottom };
    }
  });
  return box;
}

export function selectionRect() {
  const sel = window.getSelection?.();
  if (!sel || sel.isCollapsed || !sel.rangeCount) return null;
  const r = sel.getRangeAt(0).getBoundingClientRect();
  return r.width || r.height ? { left: r.left, top: r.top, right: r.right, bottom: r.bottom } : null;
}

// Copies text, falling back to a hidden textarea where the async clipboard is
// missing (older Safari, pages served over plain http on the local network).
export async function copyToClipboard(text) {
  try {
    if (navigator.clipboard?.writeText) { await navigator.clipboard.writeText(text); return true; }
  } catch { /* fall through to the old way */ }
  const ta = document.createElement('textarea');
  ta.value = text;
  ta.setAttribute('readonly', '');
  ta.style.cssText = 'position:fixed;top:0;left:0;opacity:0;';
  document.body.appendChild(ta);
  ta.select();
  let ok = false;
  try { ok = document.execCommand('copy'); } catch { ok = false; }
  document.body.removeChild(ta);
  return ok;
}
